/**
 * DaVinci Sanitizer Utility
 * 
 * Responsible for cleaning HTML content to prevent XSS attacks while allowing
 * specific Rich Text styling tags.
 * 
 * Rules:
 * 1. Allow only whitelisted tags: <b>, <strong>, <i>, <em>, <u>, <s>, <span>, <br>, <p>, <div>
 * 2. Allow only whitelisted attributes: style (for color), class (for theme)
 * 3. Strip all scripts, iframes, objects, and event handlers (onclick, etc.)
 */

export const DaVinciSanitizer = {
    // Whitelist configuration
    // Whitelist configuration
    ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 's', 'span', 'br', 'p', 'div', 'font'],
    ALLOWED_ATTRS: ['style', 'class', 'color'],

    /**
     * Sanitizes a HTML string.
     * @param {string} html - Potentially unsafe HTML string
     * @returns {string} - Cleaned HTML string
     */
    sanitize(html) {
        if (!html || typeof html !== 'string') return '';

        // 1. Create a dummy document to parse the HTML safely
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const body = doc.body;

        // 2. Recursively clean nodes
        this._cleanNode(body);

        return body.innerHTML;
    },

    /**
     * Internal recursive cleaner
     * @param {Node} node 
     */
    _cleanNode(node) {
        let child = node.firstChild;

        while (child) {
            const next = child.nextSibling;

            if (child.nodeType === Node.ELEMENT_NODE) {
                const tagName = child.tagName.toLowerCase();

                // Rule A: Remove script/iframe/object entirely
                if (['script', 'iframe', 'object', 'embed', 'style', 'link', 'meta'].includes(tagName)) {
                    node.removeChild(child);
                }
                // Rule B: Unwrap banned tags (keep text, remove tag)
                else if (!this.ALLOWED_TAGS.includes(tagName)) {
                    // Create a fragment with child's children
                    while (child.firstChild) {
                        node.insertBefore(child.firstChild, child);
                    }
                    node.removeChild(child);
                }
                // Rule C: Clean attributes for allowed tags
                else {
                    // Remove all attributes first, then restore allowed ones
                    const attrs = Array.from(child.attributes);
                    for (const attr of attrs) {
                        const name = attr.name.toLowerCase();
                        // Allow specific attributes
                        if (!this.ALLOWED_ATTRS.includes(name)) {
                            child.removeAttribute(name);
                        }
                        // Security: Check style for 'javascript:' or 'url('
                        if (name === 'style') {
                            const val = attr.value.toLowerCase();
                            if (val.includes('javascript:') || val.includes('url(') || val.includes('expression(')) {
                                child.removeAttribute('style');
                            }
                        }
                    }

                    // [Feature] Auto-Dialogue Class Injection for Rich Text
                    // If block-level element (p, div) starts with Quote, add 'type-dialogue' class.
                    // [FIX] Prevent Recursive Nesting (Double Blue Lines)
                    // Only apply to "leaf" blocks (blocks that do not contain other blocks like div/p)
                    if (['p', 'div'].includes(tagName)) {
                        const hasBlockChildren = Array.from(child.children).some(c =>
                            ['div', 'p'].includes(c.tagName.toLowerCase())
                        );

                        if (!hasBlockChildren) {
                            const text = child.textContent.trim();
                            if (text.startsWith('"') || text.startsWith('“')) {
                                child.classList.add('type-dialogue');
                                // Ensure basic paragraph class exists for styling consistency
                                child.classList.add('dv-paragraph');
                            }
                        }
                    }

                    // Recurse
                    this._cleanNode(child);
                }
            } else if (child.nodeType === Node.TEXT_NODE) {
                // Keep text defined
            } else {
                // Remove comments, etc.
                node.removeChild(child);
            }

            child = next;
        }
    },

    /**
     * Converts legacy plain text (User typed \n) to HTML
     * @param {string} text 
     * @returns {string}
     */
    convertLegacyToHtml(text) {
        if (!text || typeof text !== 'string') return '';
        // Escape HTML entities first to prevent injection from old text
        const escaped = text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

        // Convert newlines to <br> or wrap in <p>
        // Simple strategy: Replace \n with <br>
        return escaped.replace(/\n/g, '<br>');
    },

    /**
     * Detects if content mimics HTML
     * @param {string} content 
     * @returns {boolean}
     */
    isHtml(content) {
        if (!content) return false;
        // [FIX] Relaxed Check: ContentEditable creates tags anywhere, not just start/end.
        // Check for common block tags that indicate Rich Text.
        // Also checks generic HTML tag pattern if strictly needed, but explicit tags are safer.
        return /<(div|p|span|br|b|i|u|s|strong|em|img)\b/i.test(content);
    },

    /**
     * Strips HTML tags and normalizes whitespace for TTS
     * @param {string} html 
     * @returns {string} Plain text
     */
    stripForTTS(html) {
        if (!html) return '';

        // 1. Parse HTML to get text content safely
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        // Extract text content (removes tags naturally)
        const text = doc.body.textContent || "";

        // 2. Normalize Whitespace (remove multiple spaces, newlines)
        // TTS reads better with natural pauses, so we keep single newlines but trim excessive ones.
        return text.replace(/\s+/g, ' ').trim();
    }
};
